import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle02Icon, AlertCircleIcon } from '@hugeicons/core-free-icons';
import { useSearchParams } from 'react-router-dom';
import { useOAuthFinalize } from '../hooks/useOAuthFinalize';
import { Logo } from '@/components/ui/Logo';
import { retryOAuthConnect } from '../lib/retryOAuthConnect';

export function FigmaConnectedPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const finalize = useOAuthFinalize('figma', error, searchParams.get('success') === 'true');
  const isSuccess = finalize.status === 'success';

  // Sends the OPENER to the connect cards and closes this popup. The
  // destination and the reason it cannot be an in-popup navigation both live in
  // retryOAuthConnect.ts; keep them there so this page and
  // IntegrationConnectedPage cannot drift apart again.
  const handleRetry = () => {
    retryOAuthConnect();
  };

  return (
    <div className="auth-shell flex min-h-screen items-center justify-center px-4">
      <div className="auth-shell__panel">
        <div className="space-y-6 text-center">
          <Logo showText size="lg" className="justify-center" />

          {finalize.status === 'pending' ? (
            <div className="space-y-4" role="status">
              <h2 className="hero-title">Finishing your connection...</h2>
              <p className="text-sm text-gray-500">Keep this tab open while we save your connection.</p>
              {finalize.canSignIn && (
                <>
                  <p className="text-sm text-gray-500">Waiting for the OverlayQA extension. You can also sign in here to continue.</p>
                  <a className="auth-card__submit" href={finalize.signInHref}><span className="text-cap-trim">Sign in to finish</span></a>
                </>
              )}
            </div>
          ) : isSuccess ? (
            <>
              <div className="flex justify-center">
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={56} strokeWidth={2} color="currentColor" className="text-teal-400 animate-fade-in" />
              </div>
              <div className="space-y-2 animate-slide-up">
                <h2 className="hero-title">
                  Figma connected
                </h2>
                <p className="text-sm text-gray-500">
                  You can close this tab and return to the OverlayQA extension to start overlaying Figma frames.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <HugeiconsIcon icon={AlertCircleIcon} size={56} strokeWidth={2} color="currentColor" className="text-red-500 animate-fade-in" />
              </div>
              <div className="space-y-2 animate-slide-up">
                <h2 className="hero-title">
                  Connection failed
                </h2>
                <p className="text-sm text-gray-500">
                  {finalize.message || (<>
                  {error === 'missing_params' && 'The authorization request was incomplete. Please try again.'}
                  {error === 'invalid_state' && 'The authorization session expired. Please try again.'}
                  {error === 'token_exchange_failed' && 'Could not complete the Figma authorization. Please try again.'}
                  {error === 'callback_failed' && 'An unexpected error occurred. Please try again.'}
                  {error === 'access_denied' && 'Figma access was denied. Please authorize OverlayQA to connect.'}
                  {!['missing_params', 'invalid_state', 'token_exchange_failed', 'callback_failed', 'access_denied'].includes(error || '') && (
                    `Something went wrong${error ? `: ${error}` : ''}. Please try again.`
                  )}
                  </>)}
                </p>
              </div>
              {finalize.canSignIn && <a className="auth-card__submit" href={finalize.signInHref}><span className="text-cap-trim">Sign in to finish</span></a>}
              <button
                onClick={handleRetry}
                className="auth-card__submit"
              >
                <span className="text-cap-trim">Try again</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
